import { useState } from 'react';
import { format } from 'date-fns';
import { useIntl } from 'react-intl';

import Grafico from 'src/componentes/chart';
import LoadingContainer from 'src/componentes/loading-container';
import Select2 from 'src/componentes/select2';
import {
  FORMULARIO_GLOBAL_QUESTION_TYPES,
  normalizeFormularioGlobalQuestionType,
} from 'src/componentes/formulario-global/FormularioGlobalQuestionTypes';
import useAuth from 'src/hooks/useAuth';
import formatDateFns from 'src/utils/formatDateFns';
import { getDataAplicacaoFormularioGlobal } from 'src/paginas/avaliacao-desempenho/PesquisaClima/utils';
import EvolucaoFiltroAplicacoes from './EvolucaoFiltroAplicacoes';
import EvolucaoFiltroChips from './EvolucaoFiltroChips';
import useDashboardEvolucao from './useDashboardEvolucao';

const TIPOS_EVOLUCAO = [
  FORMULARIO_GLOBAL_QUESTION_TYPES.RESPOSTA_UNICA,
  FORMULARIO_GLOBAL_QUESTION_TYPES.MULTIPLA_ESCOLHA,
  FORMULARIO_GLOBAL_QUESTION_TYPES.SIMNAO,
  FORMULARIO_GLOBAL_QUESTION_TYPES.ESCALA_ESTRELAS,
  FORMULARIO_GLOBAL_QUESTION_TYPES.MATRIZ_SELECAO,
];
const CHART_COLORS = [
  '#1d7789',
  '#5aa9b5',
  '#8fd1d7',
  '#f2b01e',
  '#e9824d',
  '#8b6fc1',
  '#d65b9e',
  '#5f8fc9',
  '#66a85c',
  '#c86161',
];

const obterNumero = (value) =>
  value == null || value === '' || !Number.isFinite(Number(value))
    ? null
    : Number(value);

const obterChaveOpcao = (opcao, tipoPergunta) => {
  const chave =
    tipoPergunta === FORMULARIO_GLOBAL_QUESTION_TYPES.SIMNAO
      ? opcao?.codigo ?? opcao?.valor ?? opcao?.opcaoId
      : opcao?.opcaoId;
  return chave == null ? null : String(chave);
};

function PerguntasEvolucao({
  entidadeId,
  filtroEvolucao,
  filtros,
  perguntasAtuais,
  refreshToken,
}) {
  const intl = useIntl();
  const { user } = useAuth();
  const { currentDisplay, currentLocale } = formatDateFns(user?.idioma, true);
  const [perguntaId, setPerguntaId] = useState(null);
  const [linhaId, setLinhaId] = useState(null);
  const { dados, isLoading } = useDashboardEvolucao('perguntas', entidadeId, {
    enabled:
      !filtroEvolucao.periodoInvalido &&
      !filtroEvolucao.semAplicacoesSelecionadas,
    filtros,
    onDadosCarregados: filtroEvolucao.registrarResposta,
    parametrosEvolucao: filtroEvolucao.parametros,
    refreshToken,
  });

  const payload = dados?.model || dados;
  const aplicacoes = (
    Array.isArray(payload?.aplicacoes) ? payload.aplicacoes : []
  )
    .map((aplicacao) => ({
      ...aplicacao,
      data: getDataAplicacaoFormularioGlobal(aplicacao.dataInicio),
    }))
    .filter(
      (aplicacao) => aplicacao.data && !Number.isNaN(aplicacao.data.getTime())
    )
    .sort((a, b) => a.data - b.data || a.formularioUsoId - b.formularioUsoId);

  const perguntasPorId = new Map();
  [...(perguntasAtuais || []), ...aplicacoes.flatMap((a) => a.perguntas || [])]
    .filter((pergunta) => pergunta?.perguntaId != null)
    .forEach((pergunta) => {
      const chave = String(pergunta.perguntaId);
      const atual = perguntasPorId.get(chave);
      perguntasPorId.set(chave, {
        ...pergunta,
        ...atual,
        opcoes: atual?.opcoes?.length ? atual.opcoes : pergunta.opcoes,
        linhas: atual?.linhas?.length ? atual.linhas : pergunta.linhas,
      });
    });
  const perguntas = [...perguntasPorId.values()]
    .filter((pergunta) =>
      TIPOS_EVOLUCAO.includes(
        normalizeFormularioGlobalQuestionType(pergunta.tipoPergunta)
      )
    )
    .sort(
      (a, b) =>
        Number(a.ordemFormulario ?? a.ordem ?? 0) -
        Number(b.ordemFormulario ?? b.ordem ?? 0)
    );
  const opcoesPergunta = perguntas.map((pergunta, index) => ({
    id: pergunta.perguntaId,
    descricao: `${pergunta.ordemFormulario ?? pergunta.ordem ?? index + 1}. ${
      pergunta.titulo || `#${pergunta.perguntaId}`
    }`,
  }));
  const perguntaSelecionada =
    perguntas.find((pergunta) => String(pergunta.perguntaId) === perguntaId) ||
    perguntas[0];
  const tipoPergunta = normalizeFormularioGlobalQuestionType(
    perguntaSelecionada?.tipoPergunta
  );
  const estrelas =
    tipoPergunta === FORMULARIO_GLOBAL_QUESTION_TYPES.ESCALA_ESTRELAS;
  const matriz =
    tipoPergunta === FORMULARIO_GLOBAL_QUESTION_TYPES.MATRIZ_SELECAO;
  const respostasPorAplicacao = aplicacoes.map((aplicacao) =>
    (aplicacao.perguntas || []).find(
      (pergunta) =>
        String(pergunta.perguntaId) === String(perguntaSelecionada?.perguntaId)
    )
  );
  const linhasPorId = new Map();
  [perguntaSelecionada, ...respostasPorAplicacao].forEach((resposta) => {
    (resposta?.linhas || []).forEach((linha) => {
      const chave = String(linha.linhaId);
      if (!linhasPorId.has(chave)) linhasPorId.set(chave, linha);
    });
  });
  const linhas = [...linhasPorId.values()].sort(
    (a, b) =>
      Number(a.ordem ?? a.posicao ?? 0) - Number(b.ordem ?? b.posicao ?? 0) ||
      Number(a.posicao || 0) - Number(b.posicao || 0)
  );
  const linhaSelecionada =
    linhas.find((linha) => String(linha.linhaId) === linhaId) || linhas[0];
  const resultadosPorAplicacao = matriz
    ? respostasPorAplicacao.map((resposta) =>
        (resposta?.linhas || []).find(
          (linha) => String(linha.linhaId) === String(linhaSelecionada?.linhaId)
        )
      )
    : respostasPorAplicacao;
  const opcoesFormulario = matriz
    ? linhaSelecionada?.colunas || []
    : perguntaSelecionada?.opcoes || [];
  const obterOpcoesResultado = (resultado) =>
    matriz ? resultado?.colunas || [] : resultado?.opcoes || [];
  const opcoesPorChave = new Map();
  resultadosPorAplicacao.forEach((resposta) => {
    obterOpcoesResultado(resposta).forEach((opcao) => {
      const chave = obterChaveOpcao(opcao, tipoPergunta);
      if (chave != null && !opcoesPorChave.has(chave)) {
        opcoesPorChave.set(chave, opcao);
      }
    });
  });
  if (
    tipoPergunta !== FORMULARIO_GLOBAL_QUESTION_TYPES.SIMNAO ||
    opcoesPorChave.size === 0
  ) {
    opcoesFormulario.forEach((opcao) => {
      const chave = obterChaveOpcao(opcao, tipoPergunta);
      if (chave != null && !opcoesPorChave.has(chave)) {
        opcoesPorChave.set(chave, opcao);
      }
    });
  }
  const opcoes = [...opcoesPorChave.values()].sort(
    (a, b) =>
      Number(a.ordem ?? a.posicao ?? 0) - Number(b.ordem ?? b.posicao ?? 0) ||
      Number(a.posicao || 0) - Number(b.posicao || 0)
  );
  const quantidadeEstrelas = Math.max(
    perguntaSelecionada?.opcoes?.length || 0,
    ...respostasPorAplicacao.map((resposta) => resposta?.opcoes?.length || 0)
  );
  const numeroEstrelas = quantidadeEstrelas || 5;
  const categorias = aplicacoes.map((aplicacao) => ({
    label: format(aplicacao.data, currentDisplay, { locale: currentLocale }),
  }));

  const obterPonto = (aplicacao, valor, cor) => {
    const selecionada =
      filtros?.formularioUsoId != null &&
      String(aplicacao.formularioUsoId) === String(filtros.formularioUsoId);
    return {
      value: valor == null ? '' : String(valor),
      anchorBgColor: cor,
      anchorBorderColor: selecionada ? '#B54708' : '#FFFFFF',
      anchorBorderThickness: selecionada ? '3' : '1',
      anchorRadius: selecionada ? '8' : '5',
    };
  };

  const dataSource = estrelas
    ? {
        chart: {
          theme: 'fusion',
          bgAlpha: '0',
          canvasBgAlpha: '0',
          connectNullData: '0',
          drawCrossLine: '1',
          showBorder: '0',
          showValues: '1',
          yAxisMinValue: '1',
          yAxisMaxValue: String(numeroEstrelas),
          paletteColors: CHART_COLORS[0],
        },
        data: aplicacoes.map((aplicacao, index) => {
          const media = obterNumero(respostasPorAplicacao[index]?.media);
          return {
            label: categorias[index].label,
            ...obterPonto(
              aplicacao,
              media != null && media >= 1 ? media : null,
              CHART_COLORS[0]
            ),
          };
        }),
      }
    : {
        chart: {
          theme: 'fusion',
          bgAlpha: '0',
          canvasBgAlpha: '0',
          connectNullData: '0',
          drawCrossLine: '1',
          showBorder: '0',
          showValues: '0',
          numberSuffix: '%',
          yAxisMinValue: '0',
          yAxisMaxValue: '100',
          yAxisName: intl.formatMessage({ id: 'labelPercentual' }),
          legendPosition: 'bottom',
        },
        categories: [{ category: categorias }],
        dataset: opcoes.map((opcao, index) => {
          const cor = CHART_COLORS[index % CHART_COLORS.length];
          const chave = obterChaveOpcao(opcao, tipoPergunta);
          const opcaoFormulario = opcoesFormulario.find(
            (item) =>
              obterChaveOpcao(item, tipoPergunta) === chave ||
              (item.opcaoId != null &&
                String(item.opcaoId) === String(opcao.opcaoId))
          );
          return {
            seriesname:
              opcaoFormulario?.texto ||
              opcaoFormulario?.titulo ||
              opcao.texto ||
              opcao.titulo ||
              (opcao.codigo != null ? String(opcao.codigo) : null) ||
              intl.formatMessage(
                { id: 'labelOpcaoNumero' },
                { numero: index + 1 }
              ),
            color: cor,
            data: aplicacoes.map((aplicacao, aplicacaoIndex) => {
              const resposta = resultadosPorAplicacao[aplicacaoIndex];
              if (!resposta) return obterPonto(aplicacao, null, cor);
              if (matriz && !(obterNumero(resposta.totalRespostas) > 0)) {
                return obterPonto(aplicacao, null, cor);
              }
              const resultadoOpcao = obterOpcoesResultado(resposta).find(
                (item) => obterChaveOpcao(item, tipoPergunta) === chave
              );
              return obterPonto(
                aplicacao,
                obterNumero(resultadoOpcao?.percentual) ?? (matriz ? null : 0),
                cor
              );
            }),
          };
        }),
      };
  const possuiDados =
    !!perguntaSelecionada &&
    aplicacoes.length > 0 &&
    (estrelas
      ? respostasPorAplicacao.some(
          (resposta) => (obterNumero(resposta?.media) || 0) >= 1
        )
      : opcoes.length > 0 &&
        resultadosPorAplicacao.some((resposta) =>
          matriz
            ? obterNumero(resposta?.totalRespostas) > 0 &&
              obterOpcoesResultado(resposta).some(
                (opcao) => obterNumero(opcao.percentual) != null
              )
            : !!resposta
        ));

  return (
    <div className="tw:rounded-[9px] tw:border tw:border-solid tw:border-[#e4e7ec] tw:bg-white tw:px-5.5 tw:py-5">
      <div className="tw:mb-4.5 tw:flex tw:items-center tw:justify-between tw:gap-3">
        <div className="tw:text-lg tw:font-bold tw:text-[#101828]">
          {intl.formatMessage({ id: 'labelEvolucaoPerguntas' })}
        </div>
        <EvolucaoFiltroAplicacoes filtro={filtroEvolucao} />
      </div>
      <EvolucaoFiltroChips filtro={filtroEvolucao} />
      {!filtroEvolucao.periodoInvalido &&
        !filtroEvolucao.semAplicacoesSelecionadas && (
          <LoadingContainer isLoading={isLoading}>
            {perguntas.length > 0 && (
              <div className="tw:mb-4.5 tw:max-w-170">
                <Select2
                  clearable={false}
                  labelKey="descricao"
                  valueKey="id"
                  options={opcoesPergunta}
                  model={{
                    label: intl.formatMessage({ id: 'labelPerguntaEvolucao' }),
                    value: opcoesPergunta.find(
                      (opcao) =>
                        String(opcao.id) ===
                        String(perguntaSelecionada?.perguntaId)
                    ),
                    requestChange: (opcao) => {
                      setPerguntaId(String(opcao?.id));
                      setLinhaId(null);
                    },
                  }}
                />
              </div>
            )}
            {matriz && linhas.length > 0 && (
              <div className="tw:mb-4.5 tw:max-w-170">
                <Select2
                  clearable={false}
                  labelKey="texto"
                  valueKey="linhaId"
                  options={linhas}
                  model={{
                    label: intl.formatMessage({ id: 'labelMatrizLinhas' }),
                    value: linhaSelecionada,
                    requestChange: (linha) =>
                      setLinhaId(String(linha?.linhaId)),
                  }}
                />
              </div>
            )}
            {possuiDados ? (
              <Grafico
                dataFormat="json"
                dataSource={dataSource}
                height={360}
                type={estrelas ? 'line' : 'msline'}
                width="100%"
              />
            ) : (
              <div className="tw:px-4 tw:py-12 tw:text-center tw:text-[#98a2b3]">
                {intl.formatMessage({ id: 'msgEvolucaoAplicacoesSemDados' })}
              </div>
            )}
          </LoadingContainer>
        )}
    </div>
  );
}

export default PerguntasEvolucao;
